import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Text,
  Input,
  IconButton,
  useToast,
  Flex,
  Card,
  Badge,
  FormControl,
  FormLabel,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  VStack,
  Checkbox,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Select,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Tooltip,
  HStack,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
} from '@chakra-ui/react';
import { 
  MdAdd, 
  MdDelete, 
  MdDownload, 
  MdQrCode2, 
  MdEdit,
  MdClose,
  MdRestaurant,
  MdMeetingRoom,
  MdViewModule,
  MdLocalAtm,
  MdWc,
  MdKitchen,
  MdLocalBar,
  MdStairs,
  MdArrowUpward,
  MdShoppingCart,
  MdStore,
  MdInventory,
  MdEventSeat,
  MdCountertops,
  MdShoppingBasket,
  MdTableRestaurant,
  MdWarehouse,
  MdAccessTime,
  MdContentCopy,
  MdContentPaste,
  MdOpenWith,
} from 'react-icons/md';
import Draggable from 'react-draggable';
import { tableService } from '../../../../services/tableService';
import { floorService } from '../../../../services/floorService';
import { floorElementService } from '../../../../services/floorElementService';

const ELEMENT_TYPES = [
  { value: 'WC', label: 'WC', icon: MdWc, color: '#4A5568' },
  { value: 'CASHIER', label: 'Kasa', icon: MdLocalAtm, color: '#38A169' },
  { value: 'WINDOW', label: 'Pencere', icon: MdViewModule, color: '#3182CE' },
  { value: 'KITCHEN', label: 'Mutfak', icon: MdKitchen, color: '#E53E3E' },
  { value: 'BAR', label: 'Bar', icon: MdLocalBar, color: '#D69E2E' },
  { value: 'ENTRANCE', label: 'Kapı', icon: MdMeetingRoom, color: '#2D3748' },
  { value: 'STAIRS', label: 'Merdiven', icon: MdStairs, color: '#718096' },
  { value: 'ELEVATOR', label: 'Asansör', icon: MdArrowUpward, color: '#718096' },
  { value: 'PRODUCT_DISPLAY', label: 'Ürün Vitrini', icon: MdStore, color: '#9F7AEA' },
  { value: 'SERVICE_COUNTER', label: 'Servis Tezgahı', icon: MdRestaurant, color: '#F6AD55' },
  { value: 'STORAGE', label: 'Depo', icon: MdStore, color: '#68D391' },
  { value: 'WAITING_AREA', label: 'Bekleme Alanı', icon: MdRestaurant, color: '#FC8181' },
  { value: 'WALL', label: 'Duvar', icon: MdViewModule, color: '#000' },
];

const Tables = () => {
  const toast = useToast();
  const [floors, setFloors] = useState([]);
  const [selectedFloor, setSelectedFloor] = useState(null);
  const [tables, setTables] = useState([]);
  const [elements, setElements] = useState([]);
  const [isAddTableModalOpen, setIsAddTableModalOpen] = useState(false);
  const [isAddFloorModalOpen, setIsAddFloorModalOpen] = useState(false);
  const [isAddElementModalOpen, setIsAddElementModalOpen] = useState(false);
  const [isQRModalOpen, setIsQRModalOpen] = useState(false);
  const [selectedTable, setSelectedTable] = useState(null);
  const [selectedElement, setSelectedElement] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const canvasRef = useRef(null);
  const [resizing, setResizing] = useState({ 
    type: null, 
    id: null, 
    startX: 0, 
    startY: 0, 
    startWidth: 0, 
    startHeight: 0,
    startPosition: { x: 0, y: 0 },
    direction: null // 'nw', 'n', 'ne', 'w', 'e', 'sw', 's', 'se'
  });
  const elementRefs = useRef({});
  const tableRefs = useRef({});
  const [actualCanvasWidth, setActualCanvasWidth] = useState(1200);
  const [actualCanvasHeight, setActualCanvasHeight] = useState(800);
  
  // Copy/paste state
  const [copiedItem, setCopiedItem] = useState(null); // Tek öğe için
  
  // Delete confirmation dialogs
  const { isOpen: isDeleteTableOpen, onOpen: onDeleteTableOpen, onClose: onDeleteTableClose } = useDisclosure();
  const { isOpen: isDeleteElementOpen, onOpen: onDeleteElementOpen, onClose: onDeleteElementClose } = useDisclosure();
  const { isOpen: isDeleteFloorOpen, onOpen: onDeleteFloorOpen, onClose: onDeleteFloorClose } = useDisclosure();
  const [itemToDelete, setItemToDelete] = useState({ type: null, id: null, name: null });
  const cancelRef = useRef();

  // Form states
  const [newTableName, setNewTableName] = useState('');
  const [newTableCapacity, setNewTableCapacity] = useState('');
  const [newTableSelfService, setNewTableSelfService] = useState(false);
  const [newFloorName, setNewFloorName] = useState('');
  const [newFloorDisplayName, setNewFloorDisplayName] = useState('');
  const [newElementType, setNewElementType] = useState('WC');
  const [newElementName, setNewElementName] = useState('');

  useEffect(() => {
    fetchFloors();
  }, []);

  useEffect(() => {
    if (selectedFloor) {
      fetchFloorData(selectedFloor._id);
    }
  }, [selectedFloor]);

  // Canvas boyutlarını hesapla
  const canvasWidth = selectedFloor?.canvasWidth || 1200;
  const canvasHeight = selectedFloor?.canvasHeight || 800;

  // Canvas'ın gerçek boyutlarını güncelle
  useEffect(() => {
    const updateCanvasSize = () => {
      if (canvasRef.current) {
        setActualCanvasWidth(canvasRef.current.offsetWidth);
        setActualCanvasHeight(canvasRef.current.offsetHeight);
      }
    };

    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);
    
    // Canvas render olduktan sonra da güncelle
    const timer = setTimeout(updateCanvasSize, 100);
    
    return () => {
      window.removeEventListener('resize', updateCanvasSize);
      clearTimeout(timer);
    };
  }, [selectedFloor, canvasWidth, canvasHeight]);

  const fetchFloors = async () => {
    try {
      const response = await floorService.getFloors();
      if (response && response.success && response.data) {
        setFloors(response.data);
        if (response.data.length > 0 && !selectedFloor) {
          setSelectedFloor(response.data[0]);
        }
      } else {
        // Eğer floor yoksa boş array set et
        setFloors([]);
      }
    } catch (err) {
      // Hata durumunda boş array set et
      setFloors([]);
      toast({
        title: 'Hata',
        description: err.response?.data?.message || 'Katlar yüklenirken bir hata oluştu',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const fetchFloorData = async (floorId) => {
    if (!floorId) return;
    
    try {
      const [floorResponse, tablesResponse, elementsResponse] = await Promise.all([
        floorService.getFloor(floorId),
        tableService.getTables(floorId),
        floorElementService.getFloorElements(floorId),
      ]);

      if (floorResponse && floorResponse.success && floorResponse.data) {
        setTables(floorResponse.data.tables || []);
        setElements(elementsResponse && elementsResponse.success ? elementsResponse.data : []);
      } else {
        // Veri yoksa boş array set et
        setTables([]);
        setElements([]);
      }
    } catch (err) {
      // Hata durumunda boş array set et
      setTables([]);
      setElements([]);
      toast({
        title: 'Hata',
        description: err.response?.data?.message || 'Kat verileri yüklenirken bir hata oluştu',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleAddFloor = async (e) => {
    e.preventDefault();
    try {
      const response = await floorService.createFloor({
        name: newFloorName.trim(),
        displayName: newFloorDisplayName.trim() || newFloorName.trim(),
      });

      if (response.success) {
        await fetchFloors();
        setNewFloorName('');
        setNewFloorDisplayName('');
        setIsAddFloorModalOpen(false);
        setSelectedFloor(response.data);
        toast({
          title: 'Başarılı',
          description: 'Kat başarıyla eklendi',
          status: 'success',
          duration: 2000,
        });
      }
    } catch (err) {
      toast({
        title: 'Hata',
        description: err.response?.data?.message || 'Kat eklenirken bir hata oluştu',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleAddTable = async (e) => {
    e.preventDefault();
    if (!selectedFloor) {
      toast({
        title: 'Uyarı',
        description: 'Lütfen önce bir kat seçin',
        status: 'warning',
        duration: 2000,
      });
      return;
    }

    try {
      const tableData = {
        name: newTableName,
        capacity: parseInt(newTableCapacity),
        selfService: newTableSelfService,
        floorId: selectedFloor._id,
        position: { x: 100, y: 100 }, // Varsayılan pozisyon
        size: { width: 100, height: 100 },
      };

      const response = await tableService.addTable(tableData);
      if (response.success) {
        await fetchFloorData(selectedFloor._id);
      setNewTableName('');
      setNewTableCapacity('');
      setNewTableSelfService(false);
      setIsAddTableModalOpen(false);
      toast({
        title: 'Başarılı',
        description: 'Masa başarıyla eklendi',
        status: 'success',
        duration: 2000,
      });
      }
    } catch (err) {
      toast({
        title: 'Hata',
        description: err.response?.data?.message || 'Masa eklenirken bir hata oluştu',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleAddElement = async (e) => {
    e.preventDefault();
    if (!selectedFloor) {
      toast({
        title: 'Uyarı',
        description: 'Lütfen önce bir kat seçin',
        status: 'warning',
        duration: 2000,
      });
      return;
    }

    try {
      // Duvar için varsayılan boyutlar farklı
      const elementData = {
        type: newElementType,
        name: newElementName,
        floorId: selectedFloor._id,
        position: { x: 200, y: 200 },
        size: { width: 50, height: 50 },
      };

      const response = await floorElementService.createFloorElement(elementData);
      
      if (response && response.success) {
        await fetchFloorData(selectedFloor._id);
        setNewElementType('WC');
        setNewElementName('');
        setIsAddElementModalOpen(false);
        toast({
          title: 'Başarılı',
          description: 'Element başarıyla eklendi',
          status: 'success',
          duration: 2000,
        });
      } else {
        toast({
          title: 'Hata',
          description: response?.message || 'Element eklenirken bir hata oluştu',
          status: 'error',
          duration: 3000,
        });
      }
    } catch (err) {
      toast({
        title: 'Hata',
        description: err.response?.data?.message || err.message || 'Element eklenirken bir hata oluştu',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleTableDrag = async (tableId, position) => {
    try {
      await tableService.updateTable(tableId, { position });
      // Optimistic update
      setTables(prev => prev.map(t => 
        t._id === tableId ? { ...t, position } : t
      ));
    } catch (err) {
    }
  };

  const handleElementDrag = async (elementId, position) => {
    try {
      await floorElementService.updateFloorElement(elementId, { position });
      // Optimistic update
      setElements(prev => prev.map(e => 
        e._id === elementId ? { ...e, position } : e
      ));
    } catch (err) {
    }
  };

  const handleTableResize = async (tableId, size) => {
    try {
      // Minimum boyut kontrolü
      const minSize = 100;
      const newSize = {
        width: Math.max(size.width, minSize),
        height: Math.max(size.height, minSize)
      };
      await tableService.updateTable(tableId, { size: newSize });
      // Optimistic update
      setTables(prev => prev.map(t => 
        t._id === tableId ? { ...t, size: newSize } : t
      ));
    } catch (err) {
    }
  };

  const handleElementResize = async (elementId, size) => {
    try {
      // Minimum boyut kontrolü
      const minSize = 50;
      const newSize = {
        width: Math.max(size.width, minSize),
        height: Math.max(size.height, minSize)
      };
      await floorElementService.updateFloorElement(elementId, { size: newSize });
      // Optimistic update
      setElements(prev => prev.map(e => 
        e._id === elementId ? { ...e, size: newSize } : e
      ));
    } catch (err) {
    }
  };

  // Resize başlatma (tek handle, mouse hareketine göre 4 yön)
  const startResize = (e, type, id, currentSize, currentPosition) => {
    e.stopPropagation();
    setResizing({
      type,
      id,
      startX: e.clientX,
      startY: e.clientY,
      startWidth: currentSize.width,
      startHeight: currentSize.height,
      startPosition: currentPosition || { x: 0, y: 0 },
      direction: null // Mouse hareketine göre belirlenecek
    });
  };

  // Resize işlemi (sadece sağ alt köşeden)
  useEffect(() => {
    if (!resizing.type || !resizing.id) return;

    const handleMouseMove = (e) => {
      const deltaX = e.clientX - resizing.startX;
      const deltaY = e.clientY - resizing.startY;
      
      // Minimum boyutlar
      const minSize = resizing.type === 'table' ? 100 : 50;
      const minHeight = resizing.type === 'table' ? 100 : 50;
      
      // Mouse hareketine göre 4 yönlü resize
      // Her yöne çekince boyut artmalı, sadece pozisyon değişmeli
      let newSize = { width: resizing.startWidth, height: resizing.startHeight };
      let newPosition = { x: resizing.startPosition.x, y: resizing.startPosition.y };
      
      // X ekseni (sağ-sol) - mutlak değerle boyut artar
      const absDeltaX = Math.abs(deltaX);
      if (absDeltaX > 0) {
        if (deltaX > 0) {
          // Sağa çek: pozisyon sabit, genişlik artar
          newSize.width = Math.max(resizing.startWidth + absDeltaX, minSize);
          const maxWidth = actualCanvasWidth - resizing.startPosition.x;
          newSize.width = Math.min(newSize.width, maxWidth);
        } else {
          // Sola çek: pozisyon sola kayar, genişlik artar
          newSize.width = Math.max(resizing.startWidth + absDeltaX, minSize);
          newPosition.x = resizing.startPosition.x - absDeltaX;
          newPosition.x = Math.max(0, newPosition.x);
          const maxWidth = actualCanvasWidth - newPosition.x;
          newSize.width = Math.min(newSize.width, maxWidth);
          // Pozisyonu tekrar ayarla (boyut sınırlandıysa)
          newPosition.x = resizing.startPosition.x - (newSize.width - resizing.startWidth);
          newPosition.x = Math.max(0, newPosition.x);
        }
      }
      
      // Y ekseni (yukarı-aşağı) - mutlak değerle boyut artar
      const absDeltaY = Math.abs(deltaY);
      if (absDeltaY > 0) {
        if (deltaY > 0) {
          // Aşağıya çek: pozisyon sabit, yükseklik artar
          newSize.height = Math.max(resizing.startHeight + absDeltaY, minHeight);
          const maxHeight = actualCanvasHeight - resizing.startPosition.y;
          newSize.height = Math.min(newSize.height, maxHeight);
        } else {
          // Yukarıya çek: pozisyon yukarı kayar, yükseklik artar
          newSize.height = Math.max(resizing.startHeight + absDeltaY, minHeight);
          newPosition.y = resizing.startPosition.y - absDeltaY;
          newPosition.y = Math.max(0, newPosition.y);
          const maxHeight = actualCanvasHeight - newPosition.y;
          newSize.height = Math.min(newSize.height, maxHeight);
          // Pozisyonu tekrar ayarla (boyut sınırlandıysa)
          newPosition.y = resizing.startPosition.y - (newSize.height - resizing.startHeight);
          newPosition.y = Math.max(0, newPosition.y);
        }
      }

      // Optimistic update
      if (resizing.type === 'table') {
        setTables(prev => prev.map(t => 
          t._id === resizing.id ? { ...t, size: newSize, position: newPosition } : t
        ));
      } else {
        setElements(prev => prev.map(e => 
          e._id === resizing.id ? { ...e, size: newSize, position: newPosition } : e
        ));
      }
    };

    const handleMouseUp = () => {
      // Son boyutu ve pozisyonu kaydet
      if (resizing.type === 'table') {
        const table = tables.find(t => t._id === resizing.id);
        if (table) {
          const finalSize = {
            width: Math.max(table.size?.width || 100, 100),
            height: Math.max(table.size?.height || 100, 100)
          };
          const finalPosition = table.position || { x: 0, y: 0 };
          handleTableResize(resizing.id, finalSize);
          // Pozisyon değiştiyse güncelle
          if (finalPosition.x !== resizing.startPosition.x || finalPosition.y !== resizing.startPosition.y) {
            handleTableDrag(resizing.id, finalPosition);
          }
        }
      } else {
        const element = elements.find(e => e._id === resizing.id);
        if (element) {
          const finalSize = {
            width: Math.max(element.size?.width || 50, 50),
            height: Math.max(element.size?.height || 50, 50)
          };
          const finalPosition = element.position || { x: 0, y: 0 };
          handleElementResize(resizing.id, finalSize);
          // Pozisyon değiştiyse güncelle
          if (finalPosition.x !== resizing.startPosition.x || finalPosition.y !== resizing.startPosition.y) {
            handleElementDrag(resizing.id, finalPosition);
          }
        }
      }
      setResizing({ 
        type: null, 
        id: null, 
        startX: 0, 
        startY: 0, 
        startWidth: 0, 
        startHeight: 0,
        startPosition: { x: 0, y: 0 },
        direction: null
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizing, tables, elements, actualCanvasWidth, actualCanvasHeight]);

  const handleDeleteTable = async () => {
    if (!itemToDelete.id) return;
    try {
      await tableService.deleteTable(itemToDelete.id);
      await fetchFloorData(selectedFloor._id);
      onDeleteTableClose();
      setItemToDelete({ type: null, id: null, name: null });
      toast({
        title: 'Başarılı',
        description: 'Masa başarıyla silindi',
        status: 'success',
        duration: 2000,
      });
    } catch (err) {
      toast({
        title: 'Hata',
        description: err.response?.data?.message || 'Masa silinirken bir hata oluştu',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const confirmDeleteTable = (tableId, tableName) => {
    setItemToDelete({ type: 'table', id: tableId, name: tableName });
    onDeleteTableOpen();
  };

  const handleQRModal = (table) => {
    setSelectedTable(table);
    setIsQRModalOpen(true);
  };

  const handleDeleteElement = async () => {
    if (!itemToDelete.id) return;
    try {
      await floorElementService.deleteFloorElement(itemToDelete.id);
      await fetchFloorData(selectedFloor._id);
      onDeleteElementClose();
      setItemToDelete({ type: null, id: null, name: null });
      toast({
        title: 'Başarılı',
        description: 'Element başarıyla silindi',
        status: 'success',
        duration: 2000,
      });
    } catch (err) {
      toast({
        title: 'Hata',
        description: err.response?.data?.message || 'Element silinirken bir hata oluştu',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const confirmDeleteElement = (elementId, elementName) => {
    setItemToDelete({ type: 'element', id: elementId, name: elementName });
    onDeleteElementOpen();
  };

  const handleDeleteFloor = async () => {
    if (!itemToDelete.id) return;
    try {
      await floorService.deleteFloor(itemToDelete.id);
      await fetchFloors();
      if (selectedFloor?._id === itemToDelete.id) {
        setSelectedFloor(floors.find(f => f._id !== itemToDelete.id) || null);
      }
      onDeleteFloorClose();
      setItemToDelete({ type: null, id: null, name: null });
      toast({
        title: 'Başarılı',
        description: 'Kat başarıyla silindi',
        status: 'success',
        duration: 2000,
      });
    } catch (err) {
      toast({
        title: 'Hata',
        description: err.response?.data?.message || 'Kat silinirken bir hata oluştu',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const confirmDeleteFloor = (floorId, floorName) => {
    setItemToDelete({ type: 'floor', id: floorId, name: floorName });
    onDeleteFloorOpen();
  };

  const downloadQRCode = () => {
    if (!selectedTable?.qrCode) return;
    const downloadLink = document.createElement('a');
    downloadLink.href = selectedTable.qrCode;
    downloadLink.download = `${selectedTable.displayName}-qr.png`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  const getElementIcon = (type) => {
    const elementType = ELEMENT_TYPES.find(et => et.value === type);
    return elementType ? elementType.icon : MdRestaurant;
  };

  const getElementColor = (type) => {
    const elementType = ELEMENT_TYPES.find(et => et.value === type);
    return elementType ? elementType.color : '#808080';
  };

  // Copy/Paste fonksiyonları (sadece elementler için)
  const handleCopy = () => {
    if (!selectedElement) {
      toast({
        title: 'Uyarı',
        description: 'Kopyalamak için önce bir element seçin',
        status: 'warning',
        duration: 2000,
      });
      return;
    }

    setCopiedItem({ type: 'element', data: selectedElement });
      toast({
        title: 'Başarılı',
      description: 'Element kopyalandı',
      status: 'success',
      duration: 2000,
    });
  };

  const handlePaste = async () => {
    if (!copiedItem || !selectedFloor) {
      toast({
        title: 'Uyarı',
        description: 'Kopyalanmış element yok',
        status: 'warning',
        duration: 2000,
      });
      return;
    }

    if (copiedItem.type !== 'element') {
      toast({
        title: 'Uyarı',
        description: 'Sadece elementler kopyalanabilir',
        status: 'warning',
        duration: 2000,
      });
      return;
    }

    try {
      const elementData = {
        type: copiedItem.data.type,
        name: copiedItem.data.name ? `${copiedItem.data.name} (Kopya)` : '',
        floorId: selectedFloor._id,
        position: { 
          x: (copiedItem.data.position?.x || 0) + 50, 
          y: (copiedItem.data.position?.y || 0) + 50 
        },
        size: copiedItem.data.size || { width: 50, height: 50 },
        rotation: copiedItem.data.rotation || 0,
        color: copiedItem.data.color || '#808080',
      };
      await floorElementService.createFloorElement(elementData);
      await fetchFloorData(selectedFloor._id);
      toast({
        title: 'Başarılı',
        description: 'Element yapıştırıldı',
        status: 'success',
        duration: 2000,
      });
    } catch (err) {
      toast({
        title: 'Hata',
        description: 'Yapıştırma sırasında bir hata oluştu',
        status: 'error',
        duration: 3000,
      });
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isEditMode) return;
      
      // Ctrl/Cmd + C: Copy
      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        e.preventDefault();
        handleCopy();
      }
      
      // Ctrl/Cmd + V: Paste
      if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
        e.preventDefault();
        handlePaste();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isEditMode, selectedElement, copiedItem, selectedFloor]);

  return (
    <Card>
      <Flex direction="column" gap={4} p={6}>
        {/* Header */}
        <Flex 
          justify="space-between" 
          align="center"
          flexDirection={{ base: "column", md: "row" }}
          gap={{ base: 3, md: 0 }}
        >
          <Text fontWeight="bold" fontSize={{ base: "lg", md: "xl" }}>Masa Planı</Text>
          <HStack 
            flexWrap="wrap" 
            justify={{ base: "center", md: "flex-end" }}
            gap={2}
          >
            <Button
              size={{ base: "xs", md: "sm" }}
              colorScheme={isEditMode ? 'red' : 'gray'}
              onClick={() => setIsEditMode(!isEditMode)}
            >
              {isEditMode ? 'Düzenleme Modu' : 'Görüntüleme Modu'}
            </Button>
            {selectedFloor && (
              <>
                <Menu>
                  <MenuButton as={Button} size={{ base: "xs", md: "sm" }} colorScheme="red" leftIcon={<MdAdd />}>
                    Element Ekle
                  </MenuButton>
                  <MenuList>
                    {ELEMENT_TYPES.map((type) => {
                      const Icon = type.icon;
                      return (
                        <MenuItem
                          key={type.value}
                          icon={<Icon />}
                          onClick={() => {
                            setNewElementType(type.value);
                            setIsAddElementModalOpen(true);
                          }}
                        >
                          {type.label}
                        </MenuItem>
                      );
                    })}
                  </MenuList>
                </Menu>
                <Button
                  size={{ base: "xs", md: "sm" }}
                  colorScheme="red"
                  leftIcon={<MdAdd />}
                  onClick={() => setIsAddTableModalOpen(true)}
                >
                  Masa Ekle
                </Button>
              </>
            )}
            <Button
              size={{ base: "xs", md: "sm" }}
              colorScheme="red"
              leftIcon={<MdAdd />}
              onClick={() => setIsAddFloorModalOpen(true)}
            >
              Yeni Kat
            </Button>
          </HStack>
        </Flex>

        {/* Floor Tabs */}
        {floors.length > 0 ? (
          <Box overflowX="auto">
            <Tabs
              index={floors.findIndex(f => f._id === selectedFloor?._id)}
              onChange={(index) => setSelectedFloor(floors[index])}
            >
              <TabList minW="max-content">
                {floors.map((floor) => (
                  <Tab key={floor._id} whiteSpace="nowrap">
                    <Flex align="center" gap={2}>
                      <Text>{floor.displayName}</Text>
                      {isEditMode && (
                    <IconButton
                          icon={<MdClose />}
                          size="xs"
                      variant="ghost"
                      colorScheme="red"
                          onClick={(e) => {
                            e.stopPropagation();
                            confirmDeleteFloor(floor._id, floor.displayName);
                          }}
                        />
                      )}
                  </Flex>
                  </Tab>
                ))}
              </TabList>
            </Tabs>
          </Box>
        ) : (
          <Box textAlign="center" py={8}>
            <Text color="gray.500" mb={4}>Henüz kat eklenmemiş</Text>
                <Button
                  colorScheme="red"
              leftIcon={<MdAdd />}
              onClick={() => setIsAddFloorModalOpen(true)}
            >
              İlk Katı Ekle
                </Button>
            </Box>
        )}

        {/* Canvas */}
        {selectedFloor && (
          <Box
            overflowX="auto"
            overflowY="auto"
            maxH={{ base: "70vh", md: "80vh" }}
            border="3px solid"
            borderColor="gray.300"
            borderRadius="lg"
          >
            <Box
              ref={canvasRef}
              position="relative"
              width={`${actualCanvasWidth}px`}
              height={`${actualCanvasHeight}px`}
              minW={`${actualCanvasWidth}px`}
              minH={`${actualCanvasHeight}px`}
              bg="white"
              borderRadius="lg"
              backgroundImage="
                linear-gradient(rgba(0, 0, 0, 0.03) 1px, transparent 1px),
                linear-gradient(90deg, rgba(0, 0, 0, 0.03) 1px, transparent 1px)
              "
              backgroundSize="20px 20px"
              _before={{
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                border: '2px dashed',
                borderColor: 'gray.200',
                borderRadius: 'lg',
                pointerEvents: 'none',
                zIndex: 0
              }}
            >
            {/* Floor Plan Label */}
            <Box
              position="absolute"
              top={2}
              left={2}
              bg="red.500"
              color="white"
              px={3}
              py={1}
              borderRadius="md"
              fontSize="xs"
              fontWeight="bold"
              zIndex={1000}
              boxShadow="sm"
            >
              📐 {selectedFloor.displayName} - Kat Planı
            </Box>
            {/* Elements */}
            {elements.map((element) => {
              const ElementIcon = getElementIcon(element.type);
              const elementColor = getElementColor(element.type);
              const pos = element.position || { x: 0, y: 0 };
              const elementWidth = element.size?.width || 50;
              const elementHeight = element.size?.height || 50;
              
              // Her element için ref oluştur (sadece bir kez)
              if (!elementRefs.current[element._id]) {
                elementRefs.current[element._id] = React.createRef();
              }
              
              return (
                <Draggable
                  key={element._id}
                  nodeRef={elementRefs.current[element._id]}
                  disabled={!isEditMode}
                  position={pos}
                  bounds={{
                    left: 0,
                    top: 0,
                    right: actualCanvasWidth - elementWidth,
                    bottom: actualCanvasHeight - elementHeight
                  }}
                  onStop={(e, data) => {
                    // Canvas'ın gerçek boyutlarını al ve sınırları kontrol et
                    const maxX = Math.max(0, actualCanvasWidth - elementWidth);
                    const maxY = Math.max(0, actualCanvasHeight - elementHeight);
                    const finalX = Math.max(0, Math.min(data.x, maxX));
                    const finalY = Math.max(0, Math.min(data.y, maxY));
                    handleElementDrag(element._id, { x: finalX, y: finalY });
                  }}
                >
                  <Box
                    ref={elementRefs.current[element._id]}
                    position="absolute"
                    cursor={isEditMode ? 'move' : 'default'}
                    bg={elementColor}
                    color="white"
                    borderRadius="md"
                    p={2}
                    w={`${elementWidth}px`}
                    h={`${elementHeight}px`}
            display="flex"
            alignItems="center"
            justifyContent="center"
                    boxShadow="md"
                    border="none"
                    borderColor="transparent"
                    zIndex={1}
                    _hover={isEditMode ? { outline: '2px dashed', outlineColor: 'blue.500' } : {}}
                    onClick={(e) => {
                      if (isEditMode) {
                        e.stopPropagation();
                        setSelectedElement(element);
                        setSelectedTable(null);
                      }
                    }}
                  >
                    <VStack spacing={1}>
                      <ElementIcon size={24} />
                      {element.name && (
                        <Text fontSize="xs" fontWeight="bold">
                          {element.name}
                  </Text>
                      )}
                    </VStack>
                      {isEditMode && (
                        <>
                  <IconButton
                    icon={<MdDelete />}
                            size="xs"
                    colorScheme="red"
                            onClick={(e) => {
                              e.stopPropagation();
                              confirmDeleteElement(element._id, element.name || 'Element');
                            }}
                            position="absolute"
                            top="-8px"
                            right="-8px"
                            zIndex={10}
                          />
                          {/* Resize Handle - Tek handle, 4 yönlü */}
                          <Box
                            position="absolute"
                            bottom="-8px"
                            right="-8px"
                            w="20px"
                            h="20px"
                            bg="blue.500"
                            cursor="move"
                            borderRadius="md"
                            border="2px solid white"
                            zIndex={10}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              startResize(e, 'element', element._id, element.size || { width: 50, height: 50 }, element.position || { x: 0, y: 0 });
                            }}
                            _hover={{ bg: 'blue.600', transform: 'scale(1.1)' }}
                            transition="all 0.2s"
                          >
                            <MdOpenWith size={12} color="white" />
                  </Box>
                        </>
                      )}
                  </Box>
                </Draggable>
              );
            })}

            {/* Tables */}
            {tables.map((table) => {
              const pos = table.position || { x: 0, y: 0 };
              const statusColor = table.status === 'occupied' ? 'orange' : 
                                 table.status === 'reserved' ? 'yellow' : 'green';
              
              // Her table için ref oluştur (sadece bir kez)
              if (!tableRefs.current[table._id]) {
                tableRefs.current[table._id] = React.createRef();
              }
              
              const tableWidth = table.size?.width || 100;
              const tableHeight = table.size?.height || 100;
              
              return (
                <Draggable
                  key={table._id}
                  nodeRef={tableRefs.current[table._id]}
                  disabled={!isEditMode}
                  position={pos}
                  bounds={{
                    left: 0,
                    top: 0,
                    right: actualCanvasWidth - tableWidth,
                    bottom: actualCanvasHeight - tableHeight
                  }}
                  onStop={(e, data) => {
                    // Canvas'ın gerçek boyutlarını al ve sınırları kontrol et
                    const maxX = Math.max(0, actualCanvasWidth - tableWidth);
                    const maxY = Math.max(0, actualCanvasHeight - tableHeight);
                    const finalX = Math.max(0, Math.min(data.x, maxX));
                    const finalY = Math.max(0, Math.min(data.y, maxY));
                    handleTableDrag(table._id, { x: finalX, y: finalY });
                  }}
                >
                  <Box
                    ref={tableRefs.current[table._id]}
                    position="absolute"
                    cursor={isEditMode ? 'move' : 'pointer'}
                    bg="white"
                    border="3px solid"
                    borderColor={`${statusColor}.500`}
            borderRadius="lg"
                    p={3}
                    w={`${table.size?.width || 100}px`}
                    h={`${table.size?.height || 100}px`}
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
                    boxShadow="lg"
                    zIndex={1}
                    _hover={isEditMode ? { outline: '2px dashed', outlineColor: 'blue.500' } : {}}
                    onClick={(e) => {
                      if (isEditMode) {
                        e.stopPropagation();
                        setSelectedTable(table);
                        setSelectedElement(null);
                      } else {
                        handleQRModal(table);
                      }
                    }}
                  >
                    <Text fontWeight="bold" fontSize="sm" color="gray.700">
                      {table.displayName}
          </Text>
                    <Text fontSize="xs" color="gray.500">
                      {table.capacity} kişi
                    </Text>
                    {isEditMode && (
                      <>
                        <HStack mt={1} spacing={1}>
                          <IconButton
                            icon={<MdQrCode2 />}
                            size="xs"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleQRModal(table);
                            }}
                          />
                          <IconButton
                            icon={<MdDelete />}
                            size="xs"
              colorScheme="red"
                            onClick={(e) => {
                              e.stopPropagation();
                              confirmDeleteTable(table._id, table.displayName || table.name);
                            }}
                          />
                        </HStack>
                        {/* Resize Handle - Tek handle, 4 yönlü */}
                        <Box
                          position="absolute"
                          bottom="-8px"
                          right="-8px"
                          w="20px"
                          h="20px"
                          bg="blue.500"
                          cursor="move"
                          borderRadius="md"
                          border="2px solid white"
                          zIndex={10}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          onMouseDown={(e) => {
                            e.stopPropagation();
                            startResize(e, 'table', table._id, table.size || { width: 100, height: 100 }, table.position || { x: 0, y: 0 });
                          }}
                          _hover={{ bg: 'blue.600', transform: 'scale(1.1)' }}
                          transition="all 0.2s"
                        >
                          <MdOpenWith size={12} color="white" />
                        </Box>
                      </>
                    )}
                  </Box>
                </Draggable>
              );
            })}
            </Box>
          </Box>
        )}

        {/* Add Floor Modal */}
        <Modal isOpen={isAddFloorModalOpen} onClose={() => setIsAddFloorModalOpen(false)}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Yeni Kat Ekle</ModalHeader>
            <ModalCloseButton />
            <ModalBody py={6}>
              <form onSubmit={handleAddFloor}>
                <VStack spacing={4}>
                  <FormControl>
                    <FormLabel>Kat Adı</FormLabel>
                    <Input
                      value={newFloorName}
                      onChange={(e) => setNewFloorName(e.target.value)}
                      placeholder="Örn: zemin-kat"
                      required
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Görünen Ad</FormLabel>
                    <Input
                      value={newFloorDisplayName}
                      onChange={(e) => setNewFloorDisplayName(e.target.value)}
                      placeholder="Örn: Zemin Kat"
                    />
                  </FormControl>
                  <Button type="submit" width="full" colorScheme="red">
                    Ekle
            </Button>
                </VStack>
              </form>
            </ModalBody>
          </ModalContent>
        </Modal>

        {/* Add Table Modal */}
        <Modal isOpen={isAddTableModalOpen} onClose={() => setIsAddTableModalOpen(false)}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Yeni Masa Ekle</ModalHeader>
            <ModalCloseButton />
            <ModalBody py={6}>
              <form onSubmit={handleAddTable}>
                <VStack spacing={4}>
                  <FormControl>
                    <FormLabel>Masa Adı</FormLabel>
                    <Input
                      value={newTableSelfService ? 'Self-Servis' : newTableName}
                      onChange={(e) => {
                        if (!newTableSelfService) setNewTableName(e.target.value);
                      }}
                      placeholder="Örn: Masa-1"
                      required
                      isDisabled={newTableSelfService}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Kapasite</FormLabel>
                    <NumberInput
                      value={newTableCapacity}
                      onChange={(value) => setNewTableCapacity(value)}
                      min={1}
                      isDisabled={newTableSelfService}
                    >
                      <NumberInputField required />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>
                  <FormControl>
                    <Checkbox
                      isChecked={newTableSelfService}
                      onChange={(e) => {
                        setNewTableSelfService(e.target.checked);
                        if (e.target.checked) {
                          setNewTableCapacity('100');
                          setNewTableName('Self-Servis');
                        }
                      }}
                    >
                        Self Servis
                    </Checkbox>
                  </FormControl>
                  <Button type="submit" width="full" colorScheme="red">
                    Ekle
                  </Button>
                </VStack>
              </form>
            </ModalBody>
          </ModalContent>
        </Modal>

        {/* Add Element Modal */}
        <Modal isOpen={isAddElementModalOpen} onClose={() => setIsAddElementModalOpen(false)}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Element Ekle</ModalHeader>
            <ModalCloseButton />
            <ModalBody py={6}>
              <form onSubmit={handleAddElement}>
                <VStack spacing={4}>
                  <FormControl>
                    <FormLabel>Element Tipi</FormLabel>
                    <Select
                      value={newElementType}
                      onChange={(e) => setNewElementType(e.target.value)}
                    >
                      {ELEMENT_TYPES.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel>İsim (Opsiyonel)</FormLabel>
                    <Input
                      value={newElementName}
                      onChange={(e) => setNewElementName(e.target.value)}
                      placeholder="Örn: Ana Kapı"
                    />
                  </FormControl>
                  <Button type="submit" width="full" colorScheme="red">
                    Ekle
                  </Button>
                </VStack>
              </form>
            </ModalBody>
          </ModalContent>
        </Modal>

        {/* QR Code Modal */}
        <Modal isOpen={isQRModalOpen} onClose={() => setIsQRModalOpen(false)} isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Masa QR Kodu</ModalHeader>
            <ModalCloseButton />
            <ModalBody py={6}>
              {selectedTable && (
                <VStack spacing={4}>
                  <Text fontWeight="bold">{selectedTable.displayName || selectedTable.name}</Text>
                  {selectedTable.qrCode ? (
                    <>
                      <Box borderWidth="2px" borderStyle="dashed" p={4} borderRadius="md" bg="white" display="flex" justifyContent="center" alignItems="center">
                    <img 
                      src={selectedTable.qrCode} 
                          alt="QR Code"
                          style={{ maxWidth: '300px', height: 'auto', display: 'block' }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                    />
                  </Box>
                  <Button 
                    leftIcon={<MdDownload />} 
                    onClick={downloadQRCode}
                    colorScheme="red"
                        width="full"
                  >
                    QR Kodu İndir
                  </Button>
                    </>
                  ) : (
                    <Text color="gray.500" textAlign="center">
                      QR kod yükleniyor veya bulunamadı
                  </Text>
                  )}
                </VStack>
              )}
            </ModalBody>
          </ModalContent>
        </Modal>

        {/* Delete Table Confirmation Dialog */}
        <AlertDialog
          isOpen={isDeleteTableOpen}
          leastDestructiveRef={cancelRef}
          onClose={onDeleteTableClose}
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Masayı Sil
              </AlertDialogHeader>
              <AlertDialogBody>
                <Text>
                  <strong>{itemToDelete.name}</strong> masasını silmek istediğinize emin misiniz?
                  <br />
                  Bu işlem geri alınamaz.
                </Text>
              </AlertDialogBody>
              <AlertDialogFooter>
                <Button ref={cancelRef} onClick={onDeleteTableClose}>
                  İptal
                </Button>
                <Button colorScheme="red" onClick={handleDeleteTable} ml={3}>
                  Sil
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>

        {/* Delete Element Confirmation Dialog */}
        <AlertDialog
          isOpen={isDeleteElementOpen}
          leastDestructiveRef={cancelRef}
          onClose={onDeleteElementClose}
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Elementi Sil
              </AlertDialogHeader>
              <AlertDialogBody>
                <Text>
                  <strong>{itemToDelete.name}</strong> elementini silmek istediğinize emin misiniz?
                  <br />
                  Bu işlem geri alınamaz.
                </Text>
              </AlertDialogBody>
              <AlertDialogFooter>
                <Button ref={cancelRef} onClick={onDeleteElementClose}>
                  İptal
                </Button>
                <Button colorScheme="red" onClick={handleDeleteElement} ml={3}>
                  Sil
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>

        {/* Delete Floor Confirmation Dialog */}
        <AlertDialog
          isOpen={isDeleteFloorOpen}
          leastDestructiveRef={cancelRef}
          onClose={onDeleteFloorClose}
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Katı Sil
              </AlertDialogHeader>
              <AlertDialogBody>
                <Text>
                  <strong>{itemToDelete.name}</strong> katını silmek istediğinize emin misiniz?
                  <br />
                  Bu katın tüm masaları ve elementleri de silinecektir. Bu işlem geri alınamaz.
                </Text>
              </AlertDialogBody>
              <AlertDialogFooter>
                <Button ref={cancelRef} onClick={onDeleteFloorClose}>
                  İptal
                </Button>
                <Button colorScheme="red" onClick={handleDeleteFloor} ml={3}>
                  Sil
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      </Flex>
    </Card>
  );
};

export default Tables;
